<template>
	<div id="meetingRoom" v-loading="loading" :element-loading-text="loadingText">
		<!-- 顶部工具栏 -->
		<div class="topBar">
			<div class="meetInfo">
				<h2>{{ meetingTitle || '视频会议' }}</h2>
				<span class="badge" v-if="roomStatus === 'connected'">
					<i class="el-icon-video-camera"></i> 会议中
				</span>
				<span class="badge waiting" v-else>
					<i class="el-icon-loading"></i> 等待连接
				</span>
			</div>
			<div class="meetMeta">
				<span class="network-quality" :class="'nq-' + networkQuality" v-if="roomStatus === 'connected'" :title="'网络质量: ' + networkQualityText">
					<i class="el-icon-connection"></i> {{ networkQualityText }}
				</span>
				<span class="timer" v-if="roomStatus === 'connected'">
					<i class="el-icon-time"></i> {{ formatTime(duration) }}
				</span>
				<span class="members">
					<i class="el-icon-user"></i> {{ displayOnlineCount }} 人
				</span>
				<el-button type="danger" size="small" icon="el-icon-switch-button" @click="handleLeave">
					退出会议
				</el-button>
			</div>
		</div>

		<!-- 主区域 -->
		<div class="mainArea">
			<!-- 视频区域 -->
			<div class="videoArea" :class="{ 'gallery-mode': !hasScreenShare, 'speaker-mode': hasScreenShare }">
				<!-- 大画面：屏幕共享 或 当前发言人 -->
				<div class="mainVideo" ref="mainVideo">
					<div class="placeholder" v-if="!mainStreamUserId">
						<i class="el-icon-video-camera-solid"></i>
						<p v-if="roomStatus === 'connecting'">正在连接会议室...</p>
						<p v-else-if="roomStatus === 'idle'">正在准备会议...</p>
						<p v-else>等待加入会议...</p>
						<el-button type="primary" @click="joinRoom" v-if="roomStatus === 'idle' && !autoJoining">
							重新连接
						</el-button>
					</div>
					<div class="mainVideoLabel" v-if="mainStreamUserId">
						<span>{{ mainStreamUserId === localUserId ? '我（本地）' : mainStreamUserId }}</span>
						<el-tag size="mini" type="success" v-if="isScreenSharing && mainStreamType === 'screen'">屏幕共享</el-tag>
					</div>
					<!-- AI 实时字幕覆盖层 -->
					<div class="subtitle-overlay" v-if="subtitleEnabled && currentSubtitle">
						<div class="subtitle-text">{{ currentSubtitle }}</div>
					</div>
				</div>

				<!-- 小画面列表 (多人网格) -->
				<div class="videoGrid" :class="'grid-' + (gridRemoteUsers.length + (localStream ? 1 : 0))">
					<!-- 本地画面 -->
					<div class="videoItem local" v-if="localStream" :class="{ active: mainStreamUserId === localUserId }"
						@click="switchMainVideo(localUserId, 'camera')">
						<div :id="'local-video'" class="videoPlayer"></div>
						<div class="videoLabel">
							<span>我</span>
							<i :class="isMicOn ? 'el-icon-microphone' : 'el-icon-turn-off-microphone'"
								:style="{ color: isMicOn ? '#67c23a' : '#f56c6c' }"></i>
						</div>
					</div>
					<!-- 远端画面 -->
					<div class="videoItem" v-for="user in gridRemoteUsers" :key="user.userId"
						:class="{ active: mainStreamUserId === user.userId }"
						@click="switchMainVideo(user.userId, 'camera')">
						<div :id="'remote-video-' + user.userId" class="videoPlayer"></div>
						<div class="videoLabel">
							<span>{{ user.userId }}</span>
							<i :class="user.hasAudio ? 'el-icon-microphone' : 'el-icon-turn-off-microphone'"
								:style="{ color: user.hasAudio ? '#67c23a' : '#f56c6c' }"></i>
						</div>
					</div>
				</div>
			</div>

			<!-- 侧边栏：参会人员 + 聊天 + AI会议记录 -->
			<div class="sidebar" v-show="sidebarVisible">
				<el-tabs v-model="activeTab">
					<el-tab-pane label="成员" name="members">
						<div class="memberList">
							<el-input v-model="memberSearch" placeholder="搜索成员" size="small" prefix-icon="el-icon-search" clearable class="member-search"></el-input>
							<!-- 成员滚动区域 -->
							<div class="member-scroll-area">
								<!-- 本地用户（自己） -->
								<div class="memberItem">
									<i class="el-icon-user-solid" style="color: #409eff;"></i>
									<span class="member-name">{{ localDisplayName || localUserId }}</span>
									<el-tag size="mini" v-if="localUserId === roomOwnerUserId" type="warning">房主</el-tag>
									<el-tag size="mini" v-else-if="isAdmin" type="success">管理员</el-tag>
									<el-tag size="mini" type="info">我</el-tag>
									<el-dropdown trigger="click" size="mini">
										<el-button size="mini" type="text">更多 <i class="el-icon-arrow-up"></i></el-button>
										<el-dropdown-menu slot="dropdown">
											<el-dropdown-item @click.native="showRenameDialog(localUserId)">
												<i class="el-icon-edit"></i> 修改名称
											</el-dropdown-item>
										</el-dropdown-menu>
									</el-dropdown>
								</div>
								<!-- 远端用户 -->
								<div class="memberItem" v-for="user in filteredMembers" :key="'m-'+user.userId">
									<i class="el-icon-user-solid" style="color: #67c23a;"></i>
									<span class="member-name">{{ getMemberDisplayName(user.userId) }}</span>
									<el-tag size="mini" v-if="user.userId === roomOwnerUserId" type="warning">房主</el-tag>
									<el-tag size="mini" v-else-if="user.isAdmin" type="success">管理员</el-tag>
									<div class="member-actions" v-if="isHost || isAdmin">
										<el-dropdown trigger="click" size="mini" @command="handleMemberCommand($event, user)">
											<el-button size="mini" type="text" style="color: #606266;">
												更多 <i class="el-icon-more"></i>
											</el-button>
											<el-dropdown-menu slot="dropdown">
												<el-dropdown-item command="toggleAudio">
													<i :class="user.hasAudio ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'"></i>
													{{ user.hasAudio ? '禁言' : '解除禁言' }}
												</el-dropdown-item>
												<el-dropdown-item command="toggleVideo">
													<i :class="user.hasVideo ? 'el-icon-video-camera-solid' : 'el-icon-video-camera'"></i>
													{{ user.hasVideo ? '禁画' : '解除禁画' }}
												</el-dropdown-item>
												<el-dropdown-item command="rename">
													<i class="el-icon-edit"></i> 修改名称
												</el-dropdown-item>
												<el-dropdown-item v-if="isHost" command="setAdmin">
													<i class="el-icon-s-custom"></i> {{ user.isAdmin ? '取消管理员' : '设为管理员' }}
												</el-dropdown-item>
												<el-dropdown-item v-if="isHost" command="transferOwner">
													<i class="el-icon-user"></i> 转交房主
												</el-dropdown-item>
												<el-dropdown-item command="inviteSpeak">
													<i class="el-icon-phone-outline"></i> 邀请连麦
												</el-dropdown-item>
												<el-dropdown-item command="kick" style="color: #f56c6c;">
													<i class="el-icon-circle-close"></i> 踢出房间
												</el-dropdown-item>
											</el-dropdown-menu>
										</el-dropdown>
									</div>
								</div>
							</div>
							<!-- 房主/管理员专属控制区（底部固定） -->
							<div class="host-controls" v-if="isHost || isAdmin">
								<el-button size="small" :type="globalMuteAudio ? 'danger' : 'warning'" plain @click="muteAllAudio">
									<i class="el-icon-turn-off-microphone"></i> {{ globalMuteAudio ? '解除全体静音' : '全体静音' }}
								</el-button>
								<el-button size="small" :type="globalMuteVideo ? 'danger' : 'warning'" plain @click="muteAllVideo">
									<i class="el-icon-video-camera-solid"></i> {{ globalMuteVideo ? '解除全体禁画' : '全体禁画' }}
								</el-button>
								<el-dropdown trigger="click" @command="handleMoreCommand">
									<el-button size="small" plain>更多 <i class="el-icon-arrow-down"></i></el-button>
									<el-dropdown-menu slot="dropdown">
										<el-dropdown-item command="screenLock">
											<i class="el-icon-user"></i> {{ screenShareLocked ? '允许所有人共享屏幕' : '仅房主/管理员可共享屏幕' }}
										</el-dropdown-item>
									</el-dropdown-menu>
								</el-dropdown>
							</div>
						</div>
					</el-tab-pane>
					<el-tab-pane label="聊天" name="chat">
						<div class="chatArea">
							<div class="chatMessages" ref="chatMessages">
								<div class="chatItem" v-for="(msg, i) in chatMessages" :key="i"
									:class="{ 'chat-self': msg.user === localUserId }">
									<span class="chatUser">{{ msg.user === localUserId ? '我' : (msg.displayName || msg.user) }}：</span>
									<span class="chatText">{{ msg.text }}</span>
								</div>
							</div>
							<div class="chatInput">
								<el-input v-model="chatText" placeholder="输入消息..." size="small"
									@keyup.enter.native="sendChat">
									<el-button slot="append" icon="el-icon-s-promotion" @click="sendChat"></el-button>
								</el-input>
							</div>
						</div>
					</el-tab-pane>
					<el-tab-pane label="AI记录" name="aiRecord">
						<div class="ai-record-area">
							<div class="ai-record-header">
								<el-tag :type="subtitleEnabled ? 'success' : 'info'" size="small">
									{{ subtitleEnabled ? '正在记录' : '未开启' }}
								</el-tag>
								<el-button size="mini" type="primary" plain @click="exportMeetingRecord" v-if="meetingRecords.length > 0">
									<i class="el-icon-download"></i> 导出
								</el-button>
							</div>
							<div class="ai-record-list" ref="aiRecordList">
								<div class="ai-record-item" v-for="(record, i) in meetingRecords" :key="i">
									<div class="record-speaker">
										<el-tag size="mini">{{ record.speaker === localUserId ? '我' : record.speaker }}</el-tag>
										<span class="record-time">{{ formatRecordTime(record.time) }}</span>
									</div>
									<div class="record-content">{{ record.text }}</div>
								</div>
								<div class="ai-record-empty" v-if="meetingRecords.length === 0">
									<p>开启 AI 字幕后，会议内容将自动记录在此</p>
								</div>
							</div>
						</div>
					</el-tab-pane>
				</el-tabs>
			</div>
		</div>

		<!-- 底部控制栏 -->
		<div class="controlBar">
			<div class="controlGroup">
				<div class="controlBtn" @click="toggleMic" :class="{ off: !isMicOn }">
					<i :class="isMicOn ? 'el-icon-microphone' : 'el-icon-turn-off-microphone'"></i>
					<span>{{ isMicOn ? '静音' : '取消静音' }}</span>
				</div>
				<div class="controlBtn" @click="toggleCamera" :class="{ off: !isCameraOn }">
					<i :class="isCameraOn ? 'el-icon-video-camera' : 'el-icon-video-camera-solid'"></i>
					<span>{{ isCameraOn ? '关闭视频' : '打开视频' }}</span>
				</div>
				<div class="controlBtn" @click="toggleScreenShare" :class="{ sharing: isScreenSharing }">
					<i class="el-icon-monitor"></i>
					<span>{{ isScreenSharing ? '停止共享' : '屏幕共享' }}</span>
				</div>
				<div class="controlBtn" @click="showInviteDialog">
					<i class="el-icon-share"></i>
					<span>邀请</span>
				</div>
				<div class="controlBtn" @click="activeTab = 'chat'; sidebarVisible = true">
					<i class="el-icon-chat-dot-round"></i>
					<span>聊天</span>
				</div>
				<div class="controlBtn" @click="activeTab = 'members'; sidebarVisible = true">
					<i class="el-icon-user"></i>
					<span>成员({{ remoteUsers.length + 1 }})</span>
				</div>
				<div class="controlBtn" @click="toggleSubtitle" :class="{ active: subtitleEnabled }">
					<i class="el-icon-document"></i>
					<span>{{ subtitleEnabled ? '关闭字幕' : 'AI字幕' }}</span>
				</div>
				<div class="controlBtn" @click="activeTab = 'aiRecord'; sidebarVisible = true">
					<i class="el-icon-notebook-2"></i>
					<span>AI记录</span>
				</div>
				<div class="controlBtn" @click="manualScreenshot" v-if="isHost" :class="{ active: screenshotting }">
					<i class="el-icon-camera"></i>
					<span>{{ screenshotting ? '截图中...' : '手动截图' }}</span>
				</div>
			</div>
			<div class="controlGroup">
				<el-button round @click="handleLeave">
					<i class="el-icon-switch-button"></i> 离开会议
				</el-button>
				<el-button type="danger" round @click="handleEndMeeting" v-if="isHost">
					<i class="el-icon-video-pause"></i> 结束会议
				</el-button>
			</div>
		</div>

		<!-- 邀请弹窗 -->
		<el-dialog title="邀请参会" :visible.sync="inviteDialogVisible" width="420px" append-to-body>
			<div class="invite-content">
				<div class="invite-field">
					<label>会议主题</label>
					<div class="invite-value">{{ meetingTitle }}</div>
				</div>
				<div class="invite-field">
					<label>会议号</label>
					<div class="invite-value">{{ meetingId }}</div>
				</div>
				<div class="invite-field">
					<label>会议链接</label>
					<el-input :value="inviteLink" readonly size="small">
						<el-button slot="append" @click="copyInviteLink">复制</el-button>
					</el-input>
				</div>
			</div>
		</el-dialog>

		<!-- 修改名称弹窗 -->
		<el-dialog title="修改名称" :visible.sync="renameDialogVisible" width="360px" append-to-body>
			<el-input v-model="renameNewName" placeholder="请输入新名称" maxlength="20" show-word-limit></el-input>
			<div slot="footer">
				<el-button @click="renameDialogVisible = false">取消</el-button>
				<el-button type="primary" @click="confirmRename">确定</el-button>
			</div>
		</el-dialog>
		</div>
	</div>
</template>

<script>
	// TRTC SDK 将在运行时动态导入
	import baseUrl from '@/request/config.js';
	import axios from 'axios';
	import { getOnlineCount, startMeeting, endMeeting as endMeetingApi, getTrtcCredential } from '@/request/api.js';

	// 创建带 token 的 axios 实例（MeetingRoom 内部使用）
	const http = axios.create({ timeout: 60000 });
	http.interceptors.request.use(config => {
		const tokenStr = localStorage.getItem('token') || sessionStorage.getItem('token');
		if (tokenStr) {
			try {
				config.headers['token'] = JSON.parse(tokenStr).token;
			} catch (e) { /* ignore */ }
		}
		return config;
	});

	export default {
		name: 'MeetingRoom',
		data() {
			return {
				// 基础信息
				meetingId: null,
				meetingTitle: '',
				localUserId: '',
				isHost: false,
				isAdmin: false,
				roomOwnerUserId: '',
				roomAdminUserIds: [],

				// TRTC
				client: null,
				screenClient: null,
				localStream: null,
				screenStream: null,
				sdkAppId: 0,
				screenUserSig: '',
				userSig: '',
				roomId: 0,

				// 状态
				loading: false,
				loadingText: '',
				roomStatus: 'idle', // idle / connecting / connected
				isMicOn: true,
				isCameraOn: true,
				isScreenSharing: false,

				// 远端用户
				remoteUsers: [],
				mainStreamUserId: null,
				mainStreamType: 'camera', // camera / screen

				// 计时
				duration: 0,
				timer: null,

				// 侧栏
				sidebarVisible: true,
				activeTab: 'members',

				// 聊天
				chatMessages: [],
				chatText: '',
				chatPollTimer: null,
				lastChatTime: 0,

				// 在线人数（含虚拟）
				displayOnlineCount: 1,
				onlineCountTimer: null,

				// 网络质量 1-6, 1最好
				networkQuality: 1,

				// 断线重连
				reconnecting: false,
				reconnectAttempts: 0,
				maxReconnectAttempts: 5,

				// 成员搜索
				memberSearch: '',

				// 邀请弹窗
				inviteDialogVisible: false,

				// AI 字幕
				subtitleEnabled: false,
				currentSubtitle: '',
				speechRecognition: null,

				// AI 会议记录
				meetingRecords: [],

				// 会议状态轮询
				meetingStatusTimer: null,

				// ========== 房间管控 ==========
				roomOwnerUserId: '',
				localDisplayName: '',
				memberDisplayNames: {},
				globalMuteAudio: false,
				globalMuteVideo: false,
				screenShareLocked: false,
				commandPollTimer: null,
				renameDialogVisible: false,
				renameTargetUserId: '',
				renameNewName: '',
				// 手动截图状态
				screenshotting: false,
				// 自动加入标记
				autoJoining: false,
			}
		},
		created() {
			this.meetingId = this.$route.query.meetingId || this.$route.params.id;
			this.meetingTitle = this.$route.query.title || '视频会议';

			// 根据动态权限判断是否是房主（拥有 MEETING_CREATE 权限才可创建和管理会议，或者是 ADMIN/ASSISTANT）
			const token = JSON.parse(localStorage.getItem('token') || '{}');
			const permissions = token.permissions || [];
			this.isHost = permissions.includes('MEETING_CREATE') || token.type === 'ADMIN' || token.type === 'ASSISTANT';

			// 生成用户ID（使用唯一标识，避免中文名冲突）
			this.localUserId = token.userId
				? ('user_' + token.userId)
				: (token.phone || ('guest_' + Date.now()));

			// 异步拉取会议详情以获取标题
			this.fetchMeetingDetail();
		},
		mounted() {
			// 页面加载后自动连接会议室，无需二次点击
			this.autoJoining = true;
			this.$nextTick(() => {
				this.joinRoom().finally(() => {
					this.autoJoining = false;
				});
			});
		},
		beforeDestroy() {
			this.leaveRoom();
			if (this.timer) clearInterval(this.timer);
			if (this.onlineCountTimer) clearInterval(this.onlineCountTimer);
			if (this.chatPollTimer) clearInterval(this.chatPollTimer);
			if (this.meetingStatusTimer) clearInterval(this.meetingStatusTimer);
			if (this.commandPollTimer) clearInterval(this.commandPollTimer);
			if (this.speechRecognition) { this.speechRecognition.stop(); this.speechRecognition = null; }
		},
		computed: {
			hasScreenShare() {
				return this.isScreenSharing || this.mainStreamType === 'screen';
			},
			networkQualityText() {
				const map = { 1: '优', 2: '良', 3: '一般', 4: '差', 5: '很差', 6: '断开' };
				return map[this.networkQuality] || '未知';
			},
			/** 过滤掉屏幕共享用户（_screen 后缀） */
			displayRemoteUsers() {
				return this.remoteUsers.filter(u => !u.userId.endsWith('_screen'));
			},
			/** 显示在视频网格中的远端用户（仅限有推流、或是管理员/房主） */
			gridRemoteUsers() {
				return this.displayRemoteUsers.filter(u => u.hasStream || u.isAdmin || u.userId === this.roomOwnerUserId);
			},
			/** 成员搜索过滤 */
			filteredMembers() {
				const keyword = this.memberSearch.trim().toLowerCase();
				if (!keyword) return this.displayRemoteUsers;
				return this.displayRemoteUsers.filter(u => u.userId.toLowerCase().includes(keyword));
			},
			/** 邀请链接 */
			inviteLink() {
				return `${window.location.origin}/#/meetingRoom?meetingId=${this.meetingId}&title=${encodeURIComponent(this.meetingTitle)}`;
			}
		},
		methods: {
			// ==================== 进入/离开房间 ====================

			async fetchMeetingDetail() {
				try {
					const res = await http.get(`${baseUrl}/api/meeting/${this.meetingId}`);
					if (res.data && res.data.code === 200 && res.data.data) {
						const data = res.data.data;
						const title = data.meetingName || (data.meeting && data.meeting.meetingName);
						if (title) {
							this.meetingTitle = title;
							document.title = title;
						}
					}
				} catch (e) {
					console.warn('获取会议详情失败:', e);
				}
			},

			async joinRoom() {
				// 防止重复点击
				if (this.roomStatus === 'connecting' || this.roomStatus === 'connected') {
					this.$message.warning('正在连接或已在会议中，请勿重复操作');
					return;
				}

				// 清理上一次可能残留的连接
				await this.leaveRoom();

				this.loading = true;
				this.loadingText = '正在获取会议凭证...';
				this.roomStatus = 'connecting';

				try {
					// 计算 roomId（与后端 generateRoomId 逻辑一致）
					const calcRoomId = Number(this.meetingId) % 100000000;

					// 1. 如果是管理员/助教，先确保 TRTC 房间已创建（幂等调用）
					if (this.isHost) {
						try {
							const createRes = await http.post(`${baseUrl}/api/trtc/room/create`, {
								meetingId: Number(this.meetingId),
								title: this.meetingTitle
							});
							if (createRes.data.code === 200) {
								const roomInfo = createRes.data.data;
								this.sdkAppId = roomInfo.sdkAppId;
								this.roomId = roomInfo.roomId;
								// 注意：不再用 roomInfo.hostUserId 覆盖 localUserId
								// 每个用户使用自己的唯一ID进入房间，避免多管理员互踢
								console.log('房间已就绪: roomId=', roomInfo.roomId);
							}
						} catch (e) {
							console.warn('创建房间请求失败（可能房间已存在，继续尝试加入）:', e.message);
						}
					}

					// 2. 所有用户统一使用 credential 接口获取自己的 UserSig（每人唯一 userId）
					const credRes = await http.get(`${baseUrl}/api/trtc/credential`, {
						params: {
							userId: this.localUserId,
							roomId: this.roomId || calcRoomId
						}
					});
					if (credRes.data.code === 200) {
						const cred = credRes.data.data;
						this.sdkAppId = cred.sdkAppId;
						this.roomId = cred.roomId;
						this.userSig = cred.userSig;
					} else {
						throw new Error('获取入会凭证失败: ' + (credRes.data.msg || '未知错误'));
					}

					// 3. 初始化 TRTC 客户端
					this.loadingText = '正在连接会议室...';
					await this.initTrtc();

					this.roomStatus = 'connected';
					this.mainStreamUserId = this.localUserId;
					this.startTimer();
					this.startOnlineCountPolling();
					this.startChatPolling();
					this.startMeetingStatusPolling();
					// 初始化房间管控（房主标识、改名、禁言指令轮询）
					this.initRoomControl();
					this.$message.success('已加入会议');
				} catch (err) {
					console.error('加入会议失败:', err);
					this.$message.error('加入会议失败: ' + (err.message || '请检查TRTC配置'));
					this.roomStatus = 'idle';
				} finally {
					this.loading = false;
				}
			},

			async initTrtc() {
				// 参数校验
				if (!this.sdkAppId || this.sdkAppId === 0) {
					throw new Error('TRTC 尚未配置，请联系管理员在后端设置 trtc.sdkAppId 和 trtc.secretKey');
				}
				if (!this.userSig) {
					throw new Error('获取入会凭证失败，请重试');
				}

				// 动态导入 TRTC SDK
				const TRTC = (await import('trtc-js-sdk')).default;

				// 创建客户端（实时通话模式，所有人平等参与，无需 setRole）
				this.client = TRTC.createClient({
					sdkAppId: this.sdkAppId,
					userId: this.localUserId,
					userSig: this.userSig,
					mode: 'rtc',
					autoSubscribe: true
				});

				// 绑定事件
				this.bindTrtcEvents();

				// 进入房间
				await this.client.join({ roomId: this.roomId });

				// 只有房主默认上麦，其他管理员/观众只加入不推流
				if (this.localUserId === this.roomOwnerUserId) {
					await this.createAndPublishLocalStream(true, true);
				} else {
					this.localStream = null;
					this.isMicOn = false;
					this.isCameraOn = false;
					this.$message.info('您已作为观众进入房间，可通过底部按钮申请/开启音视频');
				}
			},

			/** 创建并发布本地音视频流（带降级机制） */
			async createAndPublishLocalStream(preferAudio, preferVideo) {
				const TRTC = (await import('trtc-js-sdk')).default;
				let streamCreated = false;
				
				// 尝试1：指定的音视频配置
				try {
					this.localStream = TRTC.createStream({
						userId: this.localUserId,
						audio: preferAudio,
						video: preferVideo
					});
					await this.localStream.initialize();
					streamCreated = true;
					this.isMicOn = preferAudio;
					this.isCameraOn = preferVideo;
				} catch (e) {
					console.warn('音视频初始化失败:', e.message);
				}

				// 尝试2：仅音频 (如果上面失败且原本需要音频)
				if (!streamCreated && preferAudio) {
					try {
						this.localStream = TRTC.createStream({
							userId: this.localUserId,
							audio: true,
							video: false
						});
						await this.localStream.initialize();
						streamCreated = true;
						this.isMicOn = true;
						this.isCameraOn = false;
						this.$message.warning('视频设备不可用，已切换为仅音频模式');
					} catch (e) {
						console.warn('仅音频初始化失败:', e.message);
					}
				}

				// 尝试3：仅视频 (如果上面失败且原本需要视频)
				if (!streamCreated && preferVideo) {
					try {
						this.localStream = TRTC.createStream({
							userId: this.localUserId,
							audio: false,
							video: true
						});
						await this.localStream.initialize();
						streamCreated = true;
						this.isMicOn = false;
						this.isCameraOn = true;
						this.$message.warning('音频设备不可用，已切换为仅视频模式');
					} catch (e) {
						console.warn('仅视频初始化失败:', e.message);
					}
				}

				if (!streamCreated) {
					this.localStream = null;
					this.isMicOn = false;
					this.isCameraOn = false;
					this.$message.info('未检测到音视频设备');
					return;
				}

				// 播放并发布
				this.$nextTick(async () => {
					if (this.localStream) {
						this.localStream.play('local-video');
						await this.client.publish(this.localStream);
					}
				});
			},

			bindTrtcEvents() {
				// 远端用户进入
				this.client.on('peer-join', (event) => {
					const userId = event.userId;
					if (!this.remoteUsers.find(u => u.userId === userId)) {
						this.remoteUsers.push({
							userId,
							hasAudio: false,
							hasVideo: false,
							hasStream: false,
							isAdmin: this.roomAdminUserIds.includes(userId)
						});
					}
					// _screen 用户不弹提示
					if (!userId.endsWith('_screen')) {
						this.$message.info(`${userId} 加入了会议`);
					}
				});

				// 远端用户离开
				this.client.on('peer-leave', (event) => {
					const userId = event.userId;
					this.remoteUsers = this.remoteUsers.filter(u => u.userId !== userId);
					if (this.mainStreamUserId === userId) {
						this.mainStreamUserId = this.localUserId;
						this.mainStreamType = 'camera';
					}
					// _screen 用户离开 = 对方停止了屏幕共享
					if (userId.endsWith('_screen')) {
						this.$message.info('对方停止了屏幕共享');
					} else {
						this.$message.warning(`${userId} 离开了会议`);
					}
				});

				// 远端流添加
				this.client.on('stream-added', (event) => {
					const remoteStream = event.stream;
					const userId = remoteStream.getUserId();
					const type = remoteStream.getType(); // 'main' | 'auxiliary'

					// 自动订阅
					this.client.subscribe(remoteStream).then(() => {
						console.log('已订阅远端流:', userId, type);
					});
				});

				// 远端流已订阅
				this.client.on('stream-subscribed', (event) => {
					const remoteStream = event.stream;
					const userId = remoteStream.getUserId();
					const type = remoteStream.getType();

					// 判断是否为屏幕共享流：auxiliary 类型 或 _screen 后缀用户的 main 流
					const isScreenShare = type === 'auxiliary' || userId.endsWith('_screen');

					if (isScreenShare) {
						// 屏幕共享流 → 直接显示在大画面
						this.$nextTick(() => {
							remoteStream.play(this.$refs.mainVideo);
							this.mainStreamUserId = userId;
							this.mainStreamType = 'screen';
						});
						this.$message.info('收到屏幕共享');
					} else {
						// 更新用户音视频状态 (必须在 nextTick 前更新，以触发 v-for 渲染 DOM)
						const user = this.remoteUsers.find(u => u.userId === userId);
						if (user) {
							user.hasAudio = remoteStream.hasAudio();
							user.hasVideo = remoteStream.hasVideo();
							user.hasStream = true;
						}

						// 视频/音频流 → 显示在小画面
						this.$nextTick(() => {
							const domId = 'remote-video-' + userId;
							remoteStream.play(domId);
						});
					}
				});

				// 远端流移除
				this.client.on('stream-removed', (event) => {
					const remoteStream = event.stream;
					const type = remoteStream.getType();
					const userId = remoteStream.getUserId();
					if (type === 'auxiliary' && this.mainStreamType === 'screen') {
						this.mainStreamType = 'camera';
					} else {
						const user = this.remoteUsers.find(u => u.userId === userId);
						if (user) {
							user.hasStream = false;
							user.hasAudio = false;
							user.hasVideo = false;
						}
					}
				});

				// 音量变化（可选：显示谁在说话）
				this.client.on('audio-volume', (event) => {
					// event.result 包含每个用户的音量
				});

				// 网络质量监听
				this.client.on('network-quality', (event) => {
					// uplinkNetworkQuality / downlinkNetworkQuality: 1-6
					this.networkQuality = Math.max(event.uplinkNetworkQuality || 1, event.downlinkNetworkQuality || 1);
				});

				// 连接状态变化（断线重连）
				this.client.on('connection-state-changed', (event) => {
					const { prevState, state } = event;
					console.log(`TRTC 连接状态: ${prevState} → ${state}`);

					if (state === 'DISCONNECTED') {
						if (this.reconnectAttempts < this.maxReconnectAttempts) {
							this.reconnecting = true;
							this.reconnectAttempts++;
							this.$message.warning(`网络断开，正在尝试重连 (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
						} else {
							this.$message.error('网络连接已断开，请检查网络后重新进入');
							this.roomStatus = 'idle';
						}
					} else if (state === 'CONNECTED') {
						if (this.reconnecting) {
							this.reconnecting = false;
							this.reconnectAttempts = 0;
							this.$message.success('网络已恢复');
						}
					}
				});
			},

			async leaveRoom() {
				try {
					if (this.localStream) {
						this.localStream.stop();
						this.localStream.close();
					}
					if (this.screenStream) {
						this.screenStream.stop();
						this.screenStream.close();
						this.screenStream = null;
					}
					if (this.screenClient) {
						await this.screenClient.leave();
						this.screenClient = null;
					}
					if (this.client) {
						await this.client.leave();
					}
				} catch (e) {
					console.warn('离开房间异常:', e);
				}
				this.roomStatus = 'idle';
				if (this.timer) clearInterval(this.timer);
			},

			// ==================== 媒体控制 ====================

			async toggleMic() {
				if (!this.localStream) {
					await this.createAndPublishLocalStream(true, this.isCameraOn);
					return;
				}
				if (this.isMicOn) {
					this.localStream.muteAudio();
				} else {
					this.localStream.unmuteAudio();
				}
				this.isMicOn = !this.isMicOn;
			},

			async toggleCamera() {
				if (!this.localStream) {
					await this.createAndPublishLocalStream(this.isMicOn, true);
					return;
				}
				if (this.isCameraOn) {
					this.localStream.muteVideo();
				} else {
					this.localStream.unmuteVideo();
				}
				this.isCameraOn = !this.isCameraOn;
			},

			async toggleScreenShare() {
				if (!this.client) return;

				if (this.isScreenSharing) {
					// ===== 停止共享 =====
					await this.cleanupScreenShare();
					this.screenUserSig = ''; // 清除缓存的凭证
					this.isScreenSharing = false;
					this.mainStreamType = 'camera';
					this.$message.info('已停止屏幕共享');
				} else {
					// ===== 开始共享 =====
					try {
						// 先清理可能残留的旧屏幕共享
						await this.cleanupScreenShare();

						const TRTC = (await import('trtc-js-sdk')).default;
						const screenUserId = this.localUserId + '_screen';

						// 获取屏幕共享用户的 userSig
						if (!this.screenUserSig) {
							const credRes = await http.get(`${baseUrl}/api/trtc/credential`, {
								params: { userId: screenUserId, roomId: this.roomId }
							});
							if (credRes.data && credRes.data.code === 200) {
								this.screenUserSig = credRes.data.data.userSig;
							} else {
								throw new Error('获取屏幕共享凭证失败');
							}
						}

						// 创建独立 client 并进房
						this.screenClient = TRTC.createClient({
							sdkAppId: this.sdkAppId,
							userId: screenUserId,
							userSig: this.screenUserSig,
							mode: 'rtc',
							autoSubscribe: false
						});
						await this.screenClient.join({ roomId: this.roomId });

						// 创建并初始化屏幕共享流
						this.screenStream = TRTC.createStream({
							userId: screenUserId,
							audio: false,
							screen: true
						});
						await this.screenStream.initialize();

						// 浏览器侧停止共享的回调
						this.screenStream.on('screen-sharing-stopped', () => {
							this.toggleScreenShare();
						});

						// 发布
						await this.screenClient.publish(this.screenStream);

						// 在大画面中显示本地屏幕共享
						this.$nextTick(() => {
							this.screenStream.play(this.$refs.mainVideo);
						});

						this.isScreenSharing = true;
						this.mainStreamType = 'screen';
						this.mainStreamUserId = screenUserId;
						this.$message.success('屏幕共享已开始');
					} catch (e) {
						await this.cleanupScreenShare();
						if (e.name === 'NotAllowedError') {
							this.$message.warning('用户取消了屏幕共享');
						} else {
							this.$message.error('屏幕共享失败: ' + e.message);
							console.error('屏幕共享失败:', e);
						}
					}
				}
			},

			/** 清理屏幕共享资源 */
			async cleanupScreenShare() {
				try {
					if (this.screenClient) {
						try { await this.screenClient.unpublish(this.screenStream); } catch (_) {}
						await this.screenClient.leave();
					}
				} catch (e) {
					console.warn('清理 screenClient:', e);
				}
				try {
					if (this.screenStream) {
						this.screenStream.stop();
						this.screenStream.close();
					}
				} catch (e) {
					console.warn('清理 screenStream:', e);
				}
				this.screenClient = null;
				this.screenStream = null;
			},

			// ==================== 手动截图（仅房主可用） ====================

			async manualScreenshot() {
				if (this.screenshotting) return;
				this.screenshotting = true;
				try {
					// 1. 从大画面容器中找到 video 元素（TRTC SDK 动态插入的）
					const mainVideoEl = this.$refs.mainVideo;
					if (!mainVideoEl) {
						this.$message.warning('未找到视频画面');
						return;
					}
					const videoEl = mainVideoEl.querySelector('video');
					if (!videoEl || videoEl.videoWidth === 0) {
						this.$message.warning('当前没有正在播放的视频画面，无法截图');
						return;
					}

					// 2. 用 canvas 截取视频当前帧
					const canvas = document.createElement('canvas');
					canvas.width = videoEl.videoWidth;
					canvas.height = videoEl.videoHeight;
					const ctx = canvas.getContext('2d');
					ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

					// 3. 叠加时间水印（右下角）
					const now = new Date();
					const timeStr = now.getFullYear() + '-' +
						String(now.getMonth() + 1).padStart(2, '0') + '-' +
						String(now.getDate()).padStart(2, '0') + ' ' +
						String(now.getHours()).padStart(2, '0') + ':' +
						String(now.getMinutes()).padStart(2, '0') + ':' +
						String(now.getSeconds()).padStart(2, '0');
					const watermark = `${this.meetingTitle || '会议直播'}  ${timeStr}`;

					// 半透明黑色背景条
					const fontSize = Math.max(16, Math.floor(canvas.width / 60));
					ctx.font = `${fontSize}px "Microsoft YaHei", sans-serif`;
					const textWidth = ctx.measureText(watermark).width;
					const padding = 12;
					const barHeight = fontSize + padding * 2;
					ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
					ctx.fillRect(0, canvas.height - barHeight, canvas.width, barHeight);
					// 白色文字
					ctx.fillStyle = '#FFFFFF';
					ctx.fillText(watermark, padding, canvas.height - padding);
					// 右侧房间号
					const roomText = `房间: ${this.roomId}`;
					const roomWidth = ctx.measureText(roomText).width;
					ctx.fillText(roomText, canvas.width - roomWidth - padding, canvas.height - padding);

					// 4. 转为 Blob 并上传
					const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
					const formData = new FormData();
					formData.append('file', blob, `screenshot_${this.meetingId}_${Date.now()}.png`);
					formData.append('meetingId', this.meetingId);
					formData.append('roomId', this.roomId || this.meetingId);

					const res = await http.post(`${baseUrl}/api/evidence/upload-screenshot`, formData, {
						headers: { 'Content-Type': 'multipart/form-data' }
					});
					if (res.data && res.data.code === 200) {
						this.$message.success('直播画面截图成功！');
					} else {
						this.$message.warning('截图上传失败: ' + (res.data.msg || ''));
					}
				} catch (e) {
					console.error('手动截图失败:', e);
					this.$message.error('截图失败: ' + (e.message || '请求异常'));
				} finally {
					this.screenshotting = false;
				}
			},

			// ==================== 主持人控制 ====================

			kickUser(userId) {
				this.$confirm(`确定要将 ${userId} 移出会议吗？`, '提示', {
					type: 'warning'
				}).then(async () => {
					try {
						await http.post(`${baseUrl}/api/meeting/${this.meetingId}/kick`, {
							targetUserId: userId,
							operatorId: this.localUserId
						});
						this.$message.success(`已踢出 ${userId}`);
					} catch (e) {
						console.error(e);
					}
				}).catch(() => {});
			},

			handleMemberCommand(cmd, user) {
				const userId = user.userId;
				if (cmd === 'toggleAudio') {
					this.toggleMemberAudio(user);
				} else if (cmd === 'toggleVideo') {
					this.toggleMemberVideo(user);
				} else if (cmd === 'rename') {
					this.showRenameDialog(userId);
				} else if (cmd === 'setAdmin') {
					this.handleSetAdmin(user);
				} else if (cmd === 'transferOwner') {
					this.handleTransferOwner(userId);
				} else if (cmd === 'inviteSpeak') {
					this.handleInviteSpeak(userId);
				} else if (cmd === 'kick') {
					this.kickUser(userId);
				}
			},

			async handleInviteSpeak(userId) {
				try {
					await http.post(`${baseUrl}/api/meeting/${this.meetingId}/invite-speak`, {
						targetUserId: userId,
						operatorId: this.localUserId
					});
					this.$message.success(`已向 ${userId} 发送连麦邀请`);
				} catch(e) {
					this.$message.error('发送邀请失败');
				}
			},

			async handleSetAdmin(user) {
				try {
					const newIsAdmin = !user.isAdmin;
					await http.post(`${baseUrl}/api/meeting/${this.meetingId}/admin`, {
						targetUserId: user.userId,
						isAdmin: newIsAdmin,
						operatorId: this.localUserId
					});
					this.$message.success(newIsAdmin ? `已将 ${user.userId} 设为管理员` : `已取消 ${user.userId} 的管理员`);
				} catch(e) {
					this.$message.error('设置管理员失败');
				}
			},

			async handleTransferOwner(userId) {
				this.$confirm(`确定要将房主权限转交给 ${userId} 吗？您将失去最高权限。`, '转交房主', {
					type: 'warning'
				}).then(async () => {
					try {
						await http.post(`${baseUrl}/api/meeting/${this.meetingId}/transfer-owner`, {
							targetUserId: userId,
							operatorId: this.localUserId
						});
						this.$message.success(`已转交房主给 ${userId}`);
					} catch(e) {
						this.$message.error('转交房主失败');
					}
				}).catch(() => {});
			},

			handleEndMeeting() {
				this.$confirm(
					'结束会议后，所有参会者将被强制退出且无法重新进入。确定要结束吗？',
					'结束会议',
					{ type: 'warning', confirmButtonText: '结束会议', confirmButtonClass: 'el-button--danger' }
				).then(async () => {
					try {
						// 通知后端结束会议（标记状态 + 广播）
						await http.post(`${baseUrl}/api/trtc/room/end`, {
							meetingId: Number(this.meetingId)
						});
						// 通知聊天频道会议已结束
						await http.post(`${baseUrl}/api/meeting/${this.meetingId}/chat`, {
							userId: 'system',
							message: '【系统通知】主持人已结束会议',
							roomId: this.roomId
						}).catch(() => {});
						this.$message.success('会议已结束');
						await this.leaveRoom();
						this.$router.go(-1);
					} catch (e) {
						console.error(e);
						this.$message.error('结束会议失败');
					}
				}).catch(() => {});
			},

			handleLeave() {
				this.$confirm(
					'离开后您可以随时重新加入。确定要离开吗？',
					'离开会议'
				).then(async () => {
					await this.leaveRoom();
					this.$router.go(-1);
				}).catch(() => {});
			},

			/** 轮询检测会议是否被主持人结束（所有参会者都需要，包括管理员） */
			startMeetingStatusPolling() {
				// 注意：不再跳过 isHost 用户。多个管理员角色都可能是 isHost，
				// 当其中一个管理员结束会议时，其他管理员也需要被通知退出。
				// 主动结束会议的人已在 handleEndMeeting 中自行退出，不会受此轮询影响。
				this.meetingStatusTimer = setInterval(async () => {
					try {
						const res = await http.get(`${baseUrl}/api/meeting/${this.meetingId}/live/status`);
						if (res.data && res.data.code === 200) {
							const status = res.data.data;
							// liveStatus: 0=未开始, 1=进行中, 2=已结束
							if (status && status.liveStatus === 2) {
								clearInterval(this.meetingStatusTimer);
								this.$alert('主持人已结束会议，您将自动退出。', '会议已结束', {
									type: 'warning',
									showClose: false,
									callback: async () => {
										await this.leaveRoom();
										this.$router.go(-1);
									}
								});
							}
						}
					} catch (e) {
						// 静默失败
					}
				}, 5000); // 每5秒检查一次
			},

			// ==================== 辅助功能 ====================

			switchMainVideo(userId, type) {
				this.mainStreamUserId = userId;
				this.mainStreamType = type;
				// TODO: 将选中的流切换到大画面
			},

			sendChat() {
				if (!this.chatText.trim()) return;
				if (!this.client) return;

				// 获取当前显示名称（改过名则用改后名，否则用 userId）
				const myName = this.localDisplayName || this.localUserId;

				const msg = {
					user: this.localUserId,
					displayName: myName,
					text: this.chatText,
					time: Date.now()
				};

				// 本地先显示
				this.chatMessages.push(msg);
				const msgText = this.chatText;
				this.chatText = '';
				this.$nextTick(() => {
					const el = this.$refs.chatMessages;
					if (el) el.scrollTop = el.scrollHeight;
				});

				// 发送到后端（异步，不阻塞UI）
				http.post(`${baseUrl}/api/meeting/${this.meetingId}/chat`, {
					userId: this.localUserId,
					displayName: myName,
					message: msgText,
					roomId: this.roomId
				}).catch(err => {
					console.warn('发送聊天消息失败:', err);
				});
			},

			/** 启动聊天消息轮询 */
			startChatPolling() {
				// 初始值设为0，首次拉取时获取该会议的所有历史聊天消息
				this.lastChatTime = 0;
				// 立即拉取一次历史消息
				this.fetchChatMessages();
				this.chatPollTimer = setInterval(() => {
					this.fetchChatMessages();
				}, 3000); // 每3秒拉取一次
			},

			async fetchChatMessages() {
				try {
					const res = await http.get(`${baseUrl}/api/meeting/${this.meetingId}/chat`, {
						params: { since: this.lastChatTime, roomId: this.roomId }
					});
					if (res.data && res.data.code === 200 && res.data.data) {
						const newMsgs = res.data.data;
						newMsgs.forEach(msg => {
							// 过滤掉自己发的（本地已显示）
							if (msg.userId !== this.localUserId) {
								this.chatMessages.push({
									user: msg.userId,
									displayName: msg.displayName || null,
									text: msg.message,
									time: msg.time
								});
							}
							if (msg.time > this.lastChatTime) {
								this.lastChatTime = msg.time;
							}
						});
						if (newMsgs.length > 0) {
							this.$nextTick(() => {
								const el = this.$refs.chatMessages;
								if (el) el.scrollTop = el.scrollHeight;
							});
						}
					}
				} catch (e) {
					// 静默失败
				}
			},

			startTimer() {
				this.duration = 0;
				this.timer = setInterval(() => {
					this.duration++;
				}, 1000);
			},

			/** 轮询在线人数（含虚拟人数） */
			startOnlineCountPolling() {
				this.fetchOnlineCount();
				this.onlineCountTimer = setInterval(() => {
					this.fetchOnlineCount();
				}, 30000); // 每30秒更新一次
			},

			async fetchOnlineCount() {
				try {
					const res = await getOnlineCount(this.meetingId);
					if (res && res.code === 200) {
						this.displayOnlineCount = res.data?.displayCount || (this.remoteUsers.length + 1);
					}
				} catch (e) {
					// 获取失败使用本地计数
					this.displayOnlineCount = this.remoteUsers.length + 1;
				}
			},

			formatTime(seconds) {
				const h = Math.floor(seconds / 3600);
				const m = Math.floor((seconds % 3600) / 60);
				const s = seconds % 60;
				return `${h > 0 ? h + ':' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
			},

			// ==================== 邀请功能 ====================

			showInviteDialog() {
				this.inviteDialogVisible = true;
			},

			copyInviteLink() {
				const text = `会议主题：${this.meetingTitle}\n会议号：${this.meetingId}\n会议链接：${this.inviteLink}`;
				navigator.clipboard.writeText(text).then(() => {
					this.$message.success('邀请信息已复制到剪贴板');
				}).catch(() => {
					// 降级方案
					const input = document.createElement('textarea');
					input.value = text;
					document.body.appendChild(input);
					input.select();
					document.execCommand('copy');
					document.body.removeChild(input);
					this.$message.success('邀请信息已复制到剪贴板');
				});
			},

			// ==================== 禁言/禁画 ====================

			toggleMemberAudio(user) {
				user.hasAudio = !user.hasAudio;
				const action = user.hasAudio ? '解除禁言' : '禁言';
				// 通过后端通知目标用户
				http.post(`${baseUrl}/api/meeting/${this.meetingId}/mute`, {
					targetUserId: user.userId,
					muteAudio: !user.hasAudio,
					operatorId: this.localUserId
				}).catch(() => {});
				this.$message.info(`已对 ${user.userId} ${action}`);
			},

			toggleMemberVideo(user) {
				user.hasVideo = !user.hasVideo;
				const action = user.hasVideo ? '解除禁画' : '禁画';
				http.post(`${baseUrl}/api/meeting/${this.meetingId}/mute`, {
					targetUserId: user.userId,
					muteVideo: !user.hasVideo,
					operatorId: this.localUserId
				}).catch(() => {});
				this.$message.info(`已对 ${user.userId} ${action}`);
			},

			muteAllAudio() {
				if (this.globalMuteAudio) {
					// 解除全体静音
					http.post(`${baseUrl}/api/meeting/${this.meetingId}/unmute-all`, {
						unmuteAudio: true,
						operatorId: this.localUserId
					}).catch(() => {});
					this.globalMuteAudio = false;
					this.$message.success('已解除全体静音');
				} else {
					this.$confirm('确定要全体静音吗？所有参会者（除您以外）将被静音。', '全体静音', { type: 'warning' }).then(() => {
						this.remoteUsers.forEach(u => { u.hasAudio = false; });
						http.post(`${baseUrl}/api/meeting/${this.meetingId}/mute-all`, {
							muteAudio: true,
							operatorId: this.localUserId
						}).catch(() => {});
						this.globalMuteAudio = true;
						this.$message.success('已全体静音');
					}).catch(() => {});
				}
			},

			muteAllVideo() {
				if (this.globalMuteVideo) {
					// 解除全体禁画
					http.post(`${baseUrl}/api/meeting/${this.meetingId}/unmute-all`, {
						unmuteVideo: true,
						operatorId: this.localUserId
					}).catch(() => {});
					this.globalMuteVideo = false;
					this.$message.success('已解除全体禁画');
				} else {
					this.$confirm('确定要全体禁画吗？所有参会者（除您以外）的摄像头将被关闭。', '全体禁画', { type: 'warning' }).then(() => {
						this.remoteUsers.forEach(u => { u.hasVideo = false; });
						http.post(`${baseUrl}/api/meeting/${this.meetingId}/mute-all`, {
							muteVideo: true,
							operatorId: this.localUserId
						}).catch(() => {});
						this.globalMuteVideo = true;
						this.$message.success('已全体禁画');
					}).catch(() => {});
				}
			},

			/** 踢出指定用户 */
			kickUser(userId) {
				if (userId === this.localUserId) {
					this.$message.warning('不能踢出自己');
					return;
				}
				this.$confirm(`确定要将 ${this.getMemberDisplayName(userId)} 踢出会议吗？`, '踢出用户', {
					type: 'warning',
					confirmButtonText: '确定踢出',
					cancelButtonText: '取消'
				}).then(async () => {
					try {
						// 通过后端 TRTC API 踢出用户
						await http.post(`${baseUrl}/api/trtc/room/kick`, {
							meetingId: Number(this.meetingId),
							userId: userId
						});
						// 本地立即移除该用户
						this.remoteUsers = this.remoteUsers.filter(u => u.userId !== userId);
						if (this.mainStreamUserId === userId) {
							this.mainStreamUserId = this.localUserId;
							this.mainStreamType = 'camera';
						}
						this.$message.success(`已将 ${this.getMemberDisplayName(userId)} 踢出会议`);
					} catch (e) {
						console.error('踢出用户失败:', e);
						this.$message.error('踢出失败: ' + (e.response?.data?.msg || e.message));
					}
				}).catch(() => {});
			},

			// ==================== AI 实时字幕 ====================

			toggleSubtitle() {
				if (this.subtitleEnabled) {
					this.stopSpeechRecognition();
					this.subtitleEnabled = false;
					this.currentSubtitle = '';
				} else {
					this.subtitleEnabled = true;
					this.startSpeechRecognition();
				}
			},

			startSpeechRecognition() {
				const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
				if (!SpeechRecognition) {
					this.$message.warning('当前浏览器不支持语音识别，请使用 Chrome 浏览器');
					this.subtitleEnabled = false;
					return;
				}

				if (this.speechRecognition) return; // 已在运行

				const recognition = new SpeechRecognition();
				recognition.continuous = true;
				recognition.interimResults = true;
				recognition.lang = 'zh-CN';
				recognition.maxAlternatives = 1;

				recognition.onresult = (event) => {
					let interimTranscript = '';
					let finalTranscript = '';

					for (let i = event.resultIndex; i < event.results.length; i++) {
						const transcript = event.results[i][0].transcript;
						if (event.results[i].isFinal) {
							finalTranscript += transcript;
						} else {
							interimTranscript += transcript;
						}
					}

					// 实时显示字幕（包含中间结果）
					this.currentSubtitle = finalTranscript || interimTranscript;

					// 最终结果记录到会议纪要
					if (finalTranscript.trim()) {
						this.meetingRecords.push({
							speaker: this.localUserId,
							text: finalTranscript.trim(),
							time: Date.now()
						});
						// 3秒后清除字幕
						setTimeout(() => {
							if (this.currentSubtitle === finalTranscript) {
								this.currentSubtitle = '';
							}
						}, 3000);
						// 滚动到最新
						this.$nextTick(() => {
							const el = this.$refs.aiRecordList;
							if (el) el.scrollTop = el.scrollHeight;
						});
					}
				};

				recognition.onerror = (event) => {
					console.warn('语音识别错误:', event.error);
					if (event.error === 'not-allowed') {
						this.$message.error('麦克风权限被拒绝，无法使用 AI 字幕');
						this.subtitleEnabled = false;
					}
				};

				recognition.onend = () => {
					// 自动重启（continuous 模式下浏览器可能中断）
					if (this.subtitleEnabled && this.speechRecognition) {
						try { recognition.start(); } catch (e) {}
					}
				};

				try {
					recognition.start();
					this.speechRecognition = recognition;
					this.$message.success('AI 字幕已开启');
				} catch (e) {
					this.$message.error('启动语音识别失败: ' + e.message);
					this.subtitleEnabled = false;
				}
			},

			stopSpeechRecognition() {
				if (this.speechRecognition) {
					this.speechRecognition.stop();
					this.speechRecognition = null;
				}
				this.$message.info('AI 字幕已关闭');
			},

			// ==================== AI 会议记录 ====================

			formatRecordTime(timestamp) {
				const d = new Date(timestamp);
				return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
			},

			exportMeetingRecord() {
				let content = `会议主题：${this.meetingTitle}\n`;
				content += `会议时间：${new Date().toLocaleString()}\n`;
				content += `会议时长：${this.formatTime(this.duration)}\n`;
				content += `参会人数：${this.remoteUsers.length + 1}\n`;
				content += '\n==================== 会议记录 ====================\n\n';

				this.meetingRecords.forEach(record => {
					const speaker = record.speaker === this.localUserId ? '我' : record.speaker;
					content += `[${this.formatRecordTime(record.time)}] ${speaker}：${record.text}\n`;
				});

				// 下载文件
				const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
				const url = URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = `会议记录_${this.meetingTitle}_${new Date().toLocaleDateString()}.txt`;
				a.click();
				URL.revokeObjectURL(url);
				this.$message.success('会议记录已导出');
			},

			// ==================== 房间管控 ====================

			/**
			 * 初始化房间管控（进房后调用）
			 * 向后端注册当前用户，获取房间状态，启动指令轮询
			 */
			async initRoomControl() {
				try {
					// 设置本地显示名称（优先用 token 中的名称）
					const token = JSON.parse(localStorage.getItem('token') || '{}');
					this.localDisplayName = token.name || token.realName || this.localUserId;

					// 向后端注册/初始化房间
					const res = await http.post(`${baseUrl}/api/meeting/${this.meetingId}/room/init`, {
						userId: this.localUserId,
						displayName: this.localDisplayName
					});

					if (res.data && res.data.code === 200 && res.data.data) {
						const state = res.data.data;
						this.roomOwnerUserId = state.ownerUserId;
						this.roomAdminUserIds = state.adminUserIds || [];
						this.globalMuteAudio = state.globalMuteAudio || false;
						this.globalMuteVideo = state.globalMuteVideo || false;
						this.screenShareLocked = state.screenShareLocked || false;

						// 更新成员显示名称映射和管理员状态
						if (state.members) {
							Object.keys(state.members).forEach(uid => {
								const member = state.members[uid];
								if (member.displayName) {
									this.$set(this.memberDisplayNames, uid, member.displayName);
								}
								const rUser = this.remoteUsers.find(u => u.userId === uid);
								if (rUser) rUser.isAdmin = member.isAdmin;
							});
						}

						// 判断是否为房主或管理员
						if (this.localUserId === state.ownerUserId) {
							this.isHost = true;
						}
						if (this.roomAdminUserIds.includes(this.localUserId)) {
							this.isAdmin = true;
						}

						console.log('房间管控初始化成功，房主:', state.ownerUserId, '当前用户:', this.localUserId);
					}

					// 启动管控指令轮询（所有用户都需要，用于接收禁言/禁画/改名等指令）
					this.startCommandPolling();
				} catch (e) {
					console.warn('初始化房间管控失败:', e);
				}
			},

			/**
			 * 启动管控指令轮询（每2秒拉取一次）
			 */
			startCommandPolling() {
				this.commandPollTimer = setInterval(() => {
					this.pollCommands();
				}, 2000);
			},

			/**
			 * 拉取管控指令并逐条执行
			 */
			async pollCommands() {
				try {
					const res = await http.get(`${baseUrl}/api/meeting/${this.meetingId}/commands`, {
						params: { userId: this.localUserId }
					});
					if (res.data && res.data.code === 200 && res.data.data) {
						res.data.data.forEach(cmd => {
							this.executeCommand(cmd);
						});
					}
				} catch (e) {
					// 静默失败
				}
			},

			/**
			 * 执行管控指令
			 * 支持的指令类型：
			 *   MUTE_AUDIO - 禁言/解除禁言
			 *   MUTE_VIDEO - 禁画/解除禁画
			 *   MUTE_ALL_AUDIO - 全体静音
			 *   MUTE_ALL_VIDEO - 全体禁画
			 *   UNMUTE_ALL_AUDIO - 解除全体静音
			 *   UNMUTE_ALL_VIDEO - 解除全体禁画
			 *   RENAME - 成员改名
			 *   SCREEN_SHARE_LOCK - 屏幕共享锁定
			 */
			executeCommand(cmd) {
				console.log('收到管控指令:', cmd.type, cmd.value);
				switch (cmd.type) {
					case 'MUTE_AUDIO':
						// 被房主禁言
						if (cmd.value === 'true') {
							if (this.localStream) this.localStream.muteAudio();
							this.isMicOn = false;
							this.$message.warning('主持人已将您静音');
						} else {
							this.$message.info('主持人已解除您的静音，您可以发言了');
						}
						break;
					case 'MUTE_VIDEO':
						// 被房主禁画
						if (cmd.value === 'true') {
							if (this.localStream) this.localStream.muteVideo();
							this.isCameraOn = false;
							this.$message.warning('主持人已关闭您的摄像头');
						} else {
							this.$message.info('主持人已允许您开启摄像头');
						}
						break;
					case 'MUTE_ALL_AUDIO':
						// 全体静音指令（房主不受影响）
						this.globalMuteAudio = true;
						if (!this.isHost) {
							if (this.localStream) this.localStream.muteAudio();
							this.isMicOn = false;
							this.$message.warning('主持人已开启全体静音');
						}
						break;
					case 'MUTE_ALL_VIDEO':
						// 全体禁画指令（房主不受影响）
						this.globalMuteVideo = true;
						if (!this.isHost) {
							if (this.localStream) this.localStream.muteVideo();
							this.isCameraOn = false;
							this.$message.warning('主持人已开启全体禁画');
						}
						break;
					case 'UNMUTE_ALL_AUDIO':
						this.globalMuteAudio = false;
						this.$message.success('主持人已解除全体静音，您可以自由发言');
						break;
					case 'UNMUTE_ALL_VIDEO':
						this.globalMuteVideo = false;
						this.$message.success('主持人已解除全体禁画，您可以开启摄像头');
						break;
					case 'RENAME':
						// cmd.value 格式：targetUserId, cmd 的其他字段包含新名称
						// 此处 targetUserId = cmd.value, 新名称 = cmd.operatorId (复用字段)
						// 实际后端 broadcastCommand 传入的是 (meetingId, null, "RENAME", targetUserId, newName)
						this.$set(this.memberDisplayNames, cmd.value, cmd.operatorId);
						if (cmd.value === this.localUserId) {
							this.localDisplayName = cmd.operatorId;
						}
						break;
					case 'SCREEN_SHARE_LOCK':
						this.screenShareLocked = cmd.value === 'true';
						if (this.screenShareLocked) {
							this.$message.info('主持人已限制仅房主可共享屏幕');
						} else {
							this.$message.info('主持人已允许所有人共享屏幕');
						}
						break;
					case 'APPLY_SPEAK':
						// 仅房主和管理员会收到
						this.$confirm(`成员 [${cmd.value}] 申请上麦连麦，是否同意？`, '上麦申请', {
							confirmButtonText: '同意',
							cancelButtonText: '拒绝',
							type: 'info'
						}).then(() => {
							http.post(`${baseUrl}/api/meeting/${this.meetingId}/approve-speak`, {
								targetUserId: cmd.value,
								approve: true,
								operatorId: this.localUserId
							});
						}).catch(() => {
							http.post(`${baseUrl}/api/meeting/${this.meetingId}/approve-speak`, {
								targetUserId: cmd.value,
								approve: false,
								operatorId: this.localUserId
							});
						});
						break;
					case 'TRANSFER_OWNER':
						this.roomOwnerUserId = cmd.value;
						this.isHost = (this.localUserId === cmd.value);
						if (this.isHost) {
							this.$message.success('您已成为新的房主');
						} else {
							this.$message.info(`房主已变更为 ${cmd.value}`);
						}
						break;
					case 'SET_ADMIN':
						const [targetId, isAdminStr] = cmd.value.split(':');
						const isAdmin = isAdminStr === 'true';
						if (isAdmin) {
							if (!this.roomAdminUserIds.includes(targetId)) this.roomAdminUserIds.push(targetId);
						} else {
							this.roomAdminUserIds = this.roomAdminUserIds.filter(id => id !== targetId);
						}
						if (targetId === this.localUserId) {
							this.isAdmin = isAdmin;
							this.$message.success(isAdmin ? '您已被设为管理员' : '您的管理员权限已被收回');
						} else {
							const ru = this.remoteUsers.find(u => u.userId === targetId);
							if (ru) ru.isAdmin = isAdmin;
						}
						break;
					case 'KICK_OUT':
						this.$alert('您已被移出会议。', '提示', {
							type: 'error',
							showClose: false,
							callback: async () => {
								await this.leaveRoom();
								this.$router.go(-1);
							}
						});
						break;
					default:
						console.warn('未知管控指令:', cmd.type);
				}
			},

			/**
			 * 获取成员显示名称（优先用 memberDisplayNames 映射）
			 */
			getMemberDisplayName(userId) {
				return this.memberDisplayNames[userId] || userId;
			},

			/**
			 * 打开修改名称弹窗
			 */
			showRenameDialog(userId) {
				this.renameTargetUserId = userId;
				this.renameNewName = userId === this.localUserId
					? (this.localDisplayName || this.localUserId)
					: (this.memberDisplayNames[userId] || userId);
				this.renameDialogVisible = true;
			},

			/**
			 * 确认修改名称
			 */
			async confirmRename() {
				const newName = this.renameNewName.trim();
				if (!newName) {
					this.$message.warning('名称不能为空');
					return;
				}
				try {
					await http.post(`${baseUrl}/api/meeting/${this.meetingId}/member/rename`, {
						targetUserId: this.renameTargetUserId,
						newDisplayName: newName,
						operatorId: this.localUserId
					});
					// 本地立即更新
					if (this.renameTargetUserId === this.localUserId) {
						this.localDisplayName = newName;
					}
					this.$set(this.memberDisplayNames, this.renameTargetUserId, newName);
					this.renameDialogVisible = false;
					this.$message.success('名称已修改');
				} catch (e) {
					this.$message.error('修改名称失败');
				}
			},

			/**
			 * 房主更多菜单命令处理
			 */
			async handleMoreCommand(command) {
				switch (command) {
					case 'screenLock':
						try {
							const newLocked = !this.screenShareLocked;
							await http.post(`${baseUrl}/api/meeting/${this.meetingId}/screen-share-lock`, {
								locked: newLocked,
								operatorId: this.localUserId
							});
							this.screenShareLocked = newLocked;
							this.$message.success(newLocked ? '已设置仅房主可共享屏幕' : '已允许所有人共享屏幕');
						} catch (e) {
							this.$message.error('操作失败');
						}
						break;
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	#meetingRoom {
		height: 100vh;
		display: flex;
		flex-direction: column;
		background: #f0f2f5;
		color: #333;
		overflow: hidden;
	}

	// ===== 顶部工具栏 =====
	.topBar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 10px 20px;
		background: #fff;
		border-bottom: 1px solid #e8e8e8;
		box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

		.meetInfo {
			display: flex;
			align-items: center;
			gap: 12px;

			h2 {
				font-size: 16px;
				font-weight: 600;
				margin: 0;
				color: #333;
			}

			.badge {
				padding: 2px 10px;
				border-radius: 12px;
				font-size: 12px;
				background: rgba(103, 194, 58, 0.1);
				color: #67c23a;

				&.waiting {
					background: rgba(230, 162, 60, 0.1);
					color: #e6a23c;
				}
			}
		}

		.meetMeta {
			display: flex;
			align-items: center;
			gap: 16px;

			.network-quality {
				font-size: 13px;
				font-weight: 500;
				padding: 4px 10px;
				border-radius: 12px;
				display: flex;
				align-items: center;
				gap: 4px;

				&.nq-1, &.nq-2 { background: #e8f8f0; color: #00b574; }
				&.nq-3 { background: #fff3e0; color: #f5a623; }
				&.nq-4, &.nq-5 { background: #fde2e2; color: #f56c6c; }
				&.nq-6 { background: #f0f1f5; color: #909399; }
			}

			.timer, .members {
				font-size: 14px;
				color: #666;
			}
		}
	}

	// ===== 主区域 =====
	.mainArea {
		flex: 1;
		display: flex;
		overflow: hidden;
	}

	// ===== 视频区域 =====
	.videoArea {
		flex: 1;
		display: flex;
		flex-direction: column;
		padding: 12px;
		gap: 12px;
		
		/* 画廊模式：没有屏幕共享时，所有人都平铺 */
		&.gallery-mode {
			.mainVideo { display: none; }
			.videoGrid {
				flex: 1;
				display: grid;
				padding: 0;
				overflow: hidden;
				/* 动态网格类 */
				&.grid-1 { grid-template-columns: 1fr; }
				&.grid-2 { grid-template-columns: 1fr 1fr; }
				&.grid-3, &.grid-4 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
				&.grid-5, &.grid-6 { grid-template-columns: 1fr 1fr 1fr; grid-template-rows: 1fr 1fr; }
				
				.videoItem {
					width: 100%;
					height: 100%;
				}
			}
		}

		/* 演讲者模式：有屏幕共享时，大图展示屏幕，下方滚动展示视频 */
		&.speaker-mode {
			.videoGrid {
				height: 120px;
				overflow-x: auto;
				flex-shrink: 0;
				.videoItem {
					width: 160px;
					height: 100px;
				}
			}
		}
	}

	.mainVideo {
		flex: 1;
		border-radius: 12px;
		overflow: hidden;
		background: #e8ecf1;
		position: relative;
		min-height: 300px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

		.placeholder {
			height: 100%;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			color: #999;

			i {
				font-size: 64px;
				margin-bottom: 16px;
				color: #bbb;
			}

			p {
				font-size: 16px;
				margin-bottom: 20px;
				color: #888;
			}
		}

		.mainVideoLabel {
			position: absolute;
			bottom: 10px;
			left: 10px;
			background: rgba(0, 0, 0, 0.55);
			padding: 4px 12px;
			border-radius: 6px;
			font-size: 13px;
			display: flex;
			align-items: center;
			gap: 8px;
			color: #fff;
		}
	}

	.videoGrid {
		display: flex;
		gap: 8px;

		.videoItem {
			flex-shrink: 0;
			border-radius: 8px;
			overflow: hidden;
			background: #dde3ea;
			position: relative;
			cursor: pointer;
			border: 2px solid transparent;
			transition: all 0.2s;
			box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

			&:hover {
				border-color: #409eff;
			}

			&.active {
				border-color: #67c23a;
			}

			&.local {
				border-color: #409eff;
			}

			.videoPlayer {
				width: 100%;
				height: 100%;
			}

			.videoLabel {
				position: absolute;
				bottom: 4px;
				left: 4px;
				right: 4px;
				display: flex;
				justify-content: space-between;
				align-items: center;
				font-size: 11px;
				background: rgba(0, 0, 0, 0.5);
				color: #fff;
				padding: 2px 6px;
				border-radius: 4px;
			}
		}
	}

	// ===== 侧边栏 =====
	.sidebar {
		width: 380px;
		min-width: 380px;
		background: #fff;
		border-left: 1px solid #e8e8e8;
		display: flex;
		flex-direction: column;

		::v-deep .el-tabs {
			height: 100%;
			display: flex;
			flex-direction: column;

			.el-tabs__header {
				padding: 0 10px;
				margin-bottom: 0;
			}

			.el-tabs__content {
				flex: 1;
				overflow: hidden;

				.el-tab-pane {
					height: 100%;
				}
			}
		}
	}

	.memberList {
		height: 100%;
		display: flex;
		flex-direction: column;
		padding: 10px;

		.member-search {
			margin-bottom: 8px;
			flex-shrink: 0;
		}

		.member-scroll-area {
			flex: 1;
			overflow-y: auto;
			min-height: 0;
		}

		.memberItem {
			display: flex;
			align-items: center;
			gap: 8px;
			padding: 8px 10px;
			border-radius: 6px;
			margin-bottom: 4px;

			&:hover {
				background: #f5f7fa;
			}

			span {
				flex: 1;
				font-size: 13px;
				color: #333;
			}
		}

		.host-controls {
			flex-shrink: 0;
			display: flex;
			gap: 8px;
			padding: 12px 0 4px;
			border-top: 1px solid #ebeef5;
			margin-top: 8px;
			flex-wrap: wrap;
		}
	}

	.chatArea {
		height: 100%;
		display: flex;
		flex-direction: column;

		.chatMessages {
			flex: 1;
			overflow-y: auto;
			padding: 10px;

			.chatItem {
				margin-bottom: 6px;
				font-size: 13px;

				.chatUser {
					color: #409eff;
					font-weight: 500;
				}

				.chatText {
					color: #555;
				}
			}
		}

		.chatInput {
			padding: 10px;
			border-top: 1px solid #e8e8e8;
		}
	}

	// ===== 底部控制栏 =====
	.controlBar {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 12px 20px;
		background: #fff;
		border-top: 1px solid #e8e8e8;
		box-shadow: 0 -1px 4px rgba(0, 0, 0, 0.04);

		.controlGroup {
			display: flex;
			align-items: center;
			gap: 24px;
		}

		.controlBtn {
			display: flex;
			flex-direction: column;
			align-items: center;
			cursor: pointer;
			transition: all 0.2s;
			padding: 6px 16px;
			border-radius: 8px;

			&:hover {
				background: #f0f2f5;
			}

			i {
				font-size: 22px;
				margin-bottom: 4px;
				color: #555;
			}

			span {
				font-size: 11px;
				color: #888;
			}

			&.off {
				i { color: #f56c6c; }
				span { color: #f56c6c; }
			}

			&.sharing {
				background: rgba(103, 194, 58, 0.1);
				i { color: #67c23a; }
				span { color: #67c23a; }
			}

			&.active {
				background: rgba(64, 158, 255, 0.1);
				i { color: #409eff; }
				span { color: #409eff; }
			}
		}
	}

	// ===== 字幕覆盖层 =====
	.subtitle-overlay {
		position: absolute;
		bottom: 60px;
		left: 50%;
		transform: translateX(-50%);
		max-width: 80%;
		z-index: 10;

		.subtitle-text {
			background: rgba(0, 0, 0, 0.7);
			color: #fff;
			padding: 8px 20px;
			border-radius: 6px;
			font-size: 16px;
			line-height: 1.6;
			text-align: center;
			backdrop-filter: blur(4px);
		}
	}

	// ===== 成员管控 =====
	.member-search { margin-bottom: 10px; }

	.host-controls {
		display: flex;
		gap: 8px;
		margin-bottom: 10px;
		padding-bottom: 10px;
		border-bottom: 1px solid #eee;
	}

	.member-name {
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.member-actions {
		display: flex;
		align-items: center;
		gap: 8px;
		i { font-size: 16px; }
	}

	// ===== 聊天样式增强 =====
	.chat-self {
		text-align: right;
		.chatUser { color: #409eff; }
	}

	// ===== AI 会议记录 =====
	.ai-record-area {
		height: calc(100vh - 200px);
		display: flex;
		flex-direction: column;
	}

	.ai-record-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 8px 0;
		border-bottom: 1px solid #eee;
		margin-bottom: 8px;
	}

	.ai-record-list {
		flex: 1;
		overflow-y: auto;
	}

	.ai-record-item {
		padding: 8px 0;
		border-bottom: 1px solid #f5f5f5;

		.record-speaker {
			display: flex;
			align-items: center;
			gap: 8px;
			margin-bottom: 4px;
		}

		.record-time {
			font-size: 12px;
			color: #999;
		}

		.record-content {
			font-size: 14px;
			line-height: 1.6;
			color: #333;
			padding-left: 4px;
		}
	}

	.ai-record-empty {
		text-align: center;
		color: #999;
		padding: 40px 0;
		font-size: 13px;
	}

	// ===== 邀请弹窗 =====
	.invite-content {
		.invite-field {
			margin-bottom: 16px;

			label {
				display: block;
				font-size: 13px;
				color: #999;
				margin-bottom: 4px;
			}

			.invite-value {
				font-size: 15px;
				color: #333;
				font-weight: 500;
			}
		}
	}
</style>
